/**
 * <combo-list> — renders combo pairs served by the Petmall middleware
 * (GET {api-base}/api/combos[?product_id=…]).
 *
 * The pairs, the discount and every money value are decided by the middleware; each combo is
 * backed by a real Shopify automatic discount, so adding both variants to the cart is enough —
 * no code, no attribute. This element only fetches, renders the cards and re-inits the slider.
 *
 * Attributes (set by sections/multiple-product-bundles.liquid):
 *   data-api-base        middleware origin, e.g. https://petmall.grind.place
 *   data-mode            "home" | "product"
 *   data-product-id      numeric Shopify product id, required when mode = product
 *   data-add-label / data-soldout-label / data-total-label / data-savings-prefix / data-savings-suffix
 */
if (!customElements.get('combo-list')) {
  customElements.define(
    'combo-list',
    class ComboList extends HTMLElement {
      connectedCallback() {
        const spinner = this.querySelector('template[data-combo-spinner]');
        this.spinnerHtml = spinner ? spinner.innerHTML : '';
        this.slider = this.querySelector('multiple-product-bundles');
        this.inner = this.querySelector('.multiple-product-bundles__inner');
        this.shopifySection = this.closest('.shopify-section');
        this.section = this.closest('.section');
        if (!this.inner) return;
        this.load();
      }

      get endpoint() {
        const base = (this.dataset.apiBase || '').replace(/\/+$/, '');
        const params = new URLSearchParams();
        // List every combo regardless of the middleware's availability flag (see comboHtml);
        // without this the homepage endpoint drops "unavailable" combos server-side.
        params.set('include_unavailable', '1');
        if (this.dataset.mode === 'product') {
          if (!this.dataset.productId) return null;
          params.set('product_id', this.dataset.productId);
        }
        const qs = params.toString();
        return `${base}/api/combos${qs ? `?${qs}` : ''}`;
      }

      async load() {
        const url = this.endpoint;
        if (!url) return this.hideSection();

        let payload = null;
        try {
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (response.ok) payload = await response.json();
        } catch (error) {
          // Never break the page over a combo strip.
          console.warn('[combos] could not load combos:', error);
        }

        const combos = payload && Array.isArray(payload.combos) ? payload.combos : [];
        if (!combos.length) return this.hideSection();

        // Cards are the theme's own offer card, rendered by Shopify (sections/combo-card.liquid)
        // so the combo looks exactly like the Liquid-rendered pairs did. Fetched in parallel.
        const cards = await Promise.all(
          combos.map((combo) => Promise.all((combo.products || []).map((p) => this.fetchCard(p))))
        );

        // Keep any image_card blocks rendered by Liquid; append the combos after them.
        this.inner.querySelectorAll('[data-combo-id]').forEach((el) => el.remove());
        const banners = this.inner.children.length;
        this.inner.insertAdjacentHTML(
          'beforeend',
          combos.map((c, i) => this.comboHtml(c, banners + i, cards[i])).join('')
        );
        this.trimCards();

        this.hidden = false;
        if (this.section) this.section.removeAttribute('data-combo-pending');
        if (this.shopifySection) this.shopifySection.hidden = false;
        this.refreshSlider(this.inner.children.length, combos.length);
        document.dispatchEvent(new CustomEvent('combos:rendered', { detail: { combos } }));
      }

      /**
       * The card comes back as the full product card. A combo is one fixed variant of a
       * one-off purchase, so the other sizes and the subscription line below the price
       * only describe something the combo cannot do: the other pills go, the one in the
       * combo stays as a label, and the subscription line follows the section setting.
       */
      trimCards() {
        const showSubscription = this.dataset.showSubscription !== 'false';

        this.inner.querySelectorAll('[data-combo-id] .opc__variants').forEach((group) => {
          const cells = [...group.querySelectorAll('.opc__variant-cell')];
          if (cells.length < 2) return;
          const keep = cells.find((cell) => cell.querySelector('.opc__variant.is-selected')) || cells[0];
          cells.forEach((cell) => {
            if (cell !== keep) cell.remove();
          });
        });

        this.inner.querySelectorAll('[data-combo-id] .opc__variant').forEach((btn) => {
          btn.setAttribute('tabindex', '-1');
          btn.setAttribute('aria-disabled', 'true');
        });

        if (showSubscription) return;
        this.inner.querySelectorAll('[data-combo-id] .opc__subscription').forEach((el) => el.remove());
        this.inner.querySelectorAll('[data-combo-id] .opc-stack--has-subscription').forEach((el) => {
          el.classList.remove('opc-stack--has-subscription');
        });
      }

      /** No combos for this page: remove the whole section, heading included. */
      hideSection() {
        if (this.shopifySection) this.shopifySection.hidden = true;
        else this.hidden = true;
      }

      /**
       * The slider counted its slides in Liquid, before these existed. Give it the real count,
       * show/hide the header arrows, then re-init it. Mobile uses the swipe strip + pagination.
       */
      refreshSlider(total, comboCount) {
        const slider = this.slider;
        if (!slider) return;
        const perView = parseInt(slider.dataset.items || '2', 10);
        const enable = total > perView;

        slider.dataset.total = String(total);
        slider.total = total;
        slider.dataset.enableSlider = enable ? 'true' : 'false';

        if (this.section) {
          this.section.querySelectorAll('[data-combo-slider-controls]').forEach((el) => {
            el.hidden = !enable;
          });
        }

        if (typeof slider.destroySlider === 'function') slider.destroySlider();
        if (typeof slider.init === 'function') slider.init();

        this.mountMobilePagination(comboCount);
      }

      mountMobilePagination(count) {
        const tpl = this.querySelector('template[data-combo-mobile-pagination]');
        if (!tpl || !this.section) return;
        this.section.querySelectorAll('scroll-pagination').forEach((el) => el.remove());
        if (count < 2) return;
        const frag = tpl.content.cloneNode(true);
        const total = frag.querySelector('[data-combo-mobile-total]');
        if (total) total.textContent = String(count);
        this.slider.insertAdjacentElement('afterend', frag.firstElementChild);
      }

      /**
       * Section Rendering API: render sections/combo-card.liquid in the product's own context,
       * with ?variant= selecting the combo's variant. Falls back to a JS-built card on failure.
       */
      productUrl(product) {
        if (!product) return null;
        if (product.url) return product.url;
        return product.handle ? `/products/${product.handle}` : null;
      }

      async fetchCard(product) {
        const productUrl = this.productUrl(product);
        if (!productUrl) return this.productHtml(product);
        try {
          const url = new URL(productUrl, window.location.origin);
          if (product.variantIdNumeric) url.searchParams.set('variant', product.variantIdNumeric);
          url.searchParams.set('sections', 'combo-card');
          // No Accept: application/json here — on /products/{handle} that makes Shopify return
          // the product JSON instead of the rendered sections.
          const response = await fetch(url.toString());
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const json = await response.json();
          const html = json && json['combo-card'];
          if (!html) throw new Error('empty section');
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const card = doc.querySelector('[data-combo-card]');
          if (!card) throw new Error('no card');
          return card.outerHTML;
        } catch (error) {
          console.warn('[combos] card render fallback:', error);
          return this.productHtml(product);
        }
      }

      // ---------- helpers ----------

      esc(value) {
        return String(value == null ? '' : value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      /** API money is a decimal string in EUR; the theme formats from cents. */
      toCents(value) {
        const n = parseFloat(value);
        return Number.isFinite(n) ? Math.round(n * 100) : 0;
      }

      /** Same format as snippets/offer-price.liquid: "3,06 €". */
      cardMoney(value) {
        return `${(this.toCents(value) / 100).toFixed(2).replace('.', ',')} €`;
      }

      /** Same format as the Liquid `money` filter used for the combo totals. */
      money(value) {
        const cents = this.toCents(value);
        if (window.FoxTheme && FoxTheme.Currency && FoxTheme.settings) {
          return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
        }
        return this.cardMoney(value);
      }

      // ---------- markup (mirrors snippets/card-product-offer.liquid, bundle_mode) ----------

      productHtml(product) {
        const productUrl = this.productUrl(product);
        const href = productUrl ? this.esc(productUrl) : '#';
        const title = this.esc(product.title || '');
        const onSale = this.toCents(product.compareAtPrice) > this.toCents(product.price);
        const priceCents = this.toCents(product.price);
        const compareCents = onSale ? this.toCents(product.compareAtPrice) : priceCents;

        const image = product.image
          ? `<img class="opc__image-img motion-reduce" src="${this.esc(product.image)}" alt="${title}" loading="lazy" width="600" height="600">`
          : '';

        const variant = product.variantTitle && product.variantTitle !== 'Default Title'
          ? `<div class="opc__variants opc__variants--fixed" role="group">
               <span class="opc__variant-cell available">
                 <button type="button" class="opc__variant is-selected" disabled aria-disabled="true"><span class="opc__variant-label">${this.esc(product.variantTitle)}</span></button>
               </span>
             </div>`
          : '';

        const price =
          `<span class="opc-price__current${onSale ? ' opc-price__current--sale' : ''}">${this.cardMoney(product.price)}</span>` +
          (onSale ? `<s class="opc-price__compare">${this.cardMoney(product.compareAtPrice)}</s>` : '');

        return `
          <div class="f-column">
            <div class="opc-stack">
              <offer-product-card
                class="opc opc--bundle${product.available ? '' : ' opc--unavailable'}"
                data-product-id="${this.esc(product.productIdNumeric)}"
                data-price="${priceCents}"
                data-compare="${compareCents}"
                data-available="${product.available ? 'true' : 'false'}"
                style="--opc-radius: ${this.esc(this.dataset.cardRadius || 16)}px;"
              >
                <div class="opc__inner">
                  <a href="${href}" class="opc__media" aria-label="${title}" tabindex="-1">
                    <div class="opc__image">${image}</div>
                  </a>
                  <div class="opc__body">
                    <h3 class="opc__title"><a href="${href}">${title}</a></h3>
                    ${variant}
                    <div class="opc__buy" data-opc-buy>
                      <div class="opc__price-row">
                        <div class="opc-price" data-opc-price>${price}</div>
                      </div>
                      <input type="hidden" class="product-variant-id" data-opc-variant-input value="${this.esc(product.variantIdNumeric)}"${product.available ? '' : ' disabled'}>
                    </div>
                  </div>
                </div>
              </offer-product-card>
            </div>
          </div>`;
      }

      comboHtml(combo, index, cardHtml) {
        const addLabel = this.esc(this.dataset.addLabel || 'Добави и двете');
        const soldOutLabel = this.esc(this.dataset.soldoutLabel || 'Изчерпано');
        const totalLabel = this.esc(this.dataset.totalLabel || 'Общо');
        const savingsPrefix = this.esc(this.dataset.savingsPrefix || 'Спестяваш');
        const savingsSuffix = this.esc(this.dataset.savingsSuffix || 'с това комбо');

        const hasSavings = this.toCents(combo.savings) > 0;
        const items = JSON.stringify((combo.addToCart && combo.addToCart.items) || []);
        // The middleware's `available` flag is not trusted for display: every combo is listed and
        // purchasable; Shopify's /cart/add.js rejects a genuinely sold-out pair atomically and
        // the error is shown under the button (bundle-combo.js).
        combo = { ...combo, available: true };
        const products = Array.isArray(combo.products) ? combo.products : [];
        const grid = Array.isArray(cardHtml) && cardHtml.length
          ? cardHtml.join('')
          : products.map((p) => this.productHtml(p)).join('');

        return `
          <div class="f-column swiper-slide" data-combo-id="${this.esc(combo.id)}" data-index="${index}">
            <bundle-combo
              class="bundle-combo h-full${combo.available ? '' : ' bundle-combo--unavailable'}"
              data-combo-id="${this.esc(combo.id)}"
              data-total="${this.esc(combo.total)}"
              data-discounted-total="${this.esc(combo.discountedTotal)}"
              data-savings="${this.esc(combo.savings)}"
              data-available="${combo.available ? 'true' : 'false'}"
              data-items="${this.esc(items)}"
            >
              <div class="bundle-combo__savings" data-bundle-savings-wrap${hasSavings ? '' : ' hidden'}>
                ${savingsPrefix} <span data-bundle-savings>${this.money(combo.savings)}</span> ${savingsSuffix}
              </div>

              <div class="bundle-combo__grid f-grid f-grid--gap-extra-small f-grid--row-gap-inherit">
                ${grid}
              </div>

              <div class="bundle-combo__footer">
                <div class="bundle-combo__total">
                  <span class="bundle-combo__total-label">${totalLabel}</span>
                  <span class="bundle-combo__total-value${hasSavings ? ' has-savings' : ''}" data-bundle-total>${this.money(combo.discountedTotal)}</span>
                </div>
                <button type="button" class="bundle-combo__add btn btn--primary" data-bundle-add${combo.available ? '' : ' disabled aria-disabled="true"'}>
                  <span class="btn__text">${combo.available ? addLabel : soldOutLabel}</span>
                  ${this.spinnerHtml}
                </button>
              </div>

              <div class="product-form__error-message blocks-radius-md alert alert--error" role="alert" data-bundle-error hidden></div>
            </bundle-combo>
          </div>`;
      }
    }
  );
}
